import React from 'react';

import { SyncAlt } from '@mui/icons-material';
import { Stack } from '@mui/material';

import { AppSettings } from '@/app/AppSettings';
import {
  HighlightableJson,
  makeHighlightableJson,
} from '@/app/log-viewer/highlighting/HighlightableJson';
import {
  HighlightableString,
  makeHighlightableString,
  makeHighlightableStringWithParts,
} from '@/app/log-viewer/highlighting/HighlightableString';
import { HighlightableStringExpandableContainerState } from '@/app/log-viewer/highlighting/HighlightableStringExpandableContainer';
import { HighlightableStringView } from '@/app/log-viewer/highlighting/HighlightableStringView';
import { LogEntry } from '@/parsing/LogEntry';

import { LogEntryCellModel, LogEntryCellModelData, LogEntryCellType } from '../LogEntryCellModel';
import { NetworkRequestBody, NetworkRequestBodyModelData } from './components/NetworkRequestBody';

export interface LogEntryMQTTCellModelData extends LogEntryCellModelData {
  content: HighlightableString;
  title: HighlightableString;
  command: HighlightableString;
  message?: NetworkRequestBodyModelData;
  params?: HighlightableString[];
}

export interface LogEntryMQTTCellModel extends LogEntryCellModel {
  data: LogEntryMQTTCellModelData;
  onLogEntryCellModelChange?: (args: {
    cellModel: LogEntryCellModel;
    change: DeepPartial<LogEntryMQTTCellModelData>;
    postProcess: boolean;
  }) => void;
  onRequestCreateNewColumn?: (jqQuery: string) => void;
}

export abstract class LogEntryMQTTCellModelFactory {
  static make(logEntry: LogEntry): LogEntryMQTTCellModel {
    return new LogEntryMQTTCellModelImpl(logEntry);
  }
}

export class LogEntryMQTTCellModelImpl implements LogEntryMQTTCellModel {
  data: LogEntryMQTTCellModelData;
  onLogEntryCellModelChange?: (args: {
    cellModel: LogEntryCellModel;
    change: DeepPartial<LogEntryMQTTCellModelData>;
    postProcess: boolean;
  }) => void;

  constructor(logEntry: LogEntry) {
    const mqttMessage = logEntry.extra.mqttMessage;

    if (!mqttMessage) {
      // Defensive fallback: an entry was routed here without a parsed MQTT
      // message. Render its raw content instead of throwing and taking the
      // whole table down.
      this.data = {
        type: LogEntryCellType.MQTTMessage,
        content: makeHighlightableString(logEntry.content),
        title: makeHighlightableString(''),
        command: makeHighlightableString(''),
        message: undefined,
        params: [],
      };
      return;
    }

    const urlParts = mqttMessage.url
      ? [
          {
            content: mqttMessage.url,
            cssClasses: ['url', 'url-prefix'],
          },
          {
            content: '',
            cssClasses: ['url', 'url-space-before-path'],
          },
          mqttMessage.params.topic && {
            content: mqttMessage.params.topic,
            cssClasses: ['url'],
          },
        ].filter(Boolean)
      : [];

    const messageContent = mqttMessage.params.message
      ? makeHighlightableJson(formatMessage(mqttMessage.params.message))
      : undefined;

    const filteredParams = Object.entries(mqttMessage.params)
      .filter(([key]) => key !== 'topic' && key !== 'message')
      .map(([key, value]) => {
        const paramParts = [
          {
            content: key,
            cssClasses: ['propertyKey'],
          },
          {
            content: ': ',
            cssClasses: [],
          },
          {
            content: `${value}`,
            cssClasses: ['propertyValue'],
          },
        ];
        return makeHighlightableStringWithParts(...paramParts);
      });

    this.data = {
      type: LogEntryCellType.MQTTMessage,
      content: makeHighlightableString(logEntry.content),
      title: urlParts.length
        ? makeHighlightableStringWithParts(...urlParts)
        : makeHighlightableString(''), // fallback if no URL
      command: makeHighlightableString(mqttMessage.command),
      message: this.createBodyData(messageContent),
      params: filteredParams,
    };
  }

  applySettings(_s: AppSettings) {}

  private createBodyData(bodyJson?: HighlightableJson): NetworkRequestBodyModelData | undefined {
    if (!bodyJson) {
      return undefined;
    }

    const title = makeHighlightableString('Message');
    return {
      title,
      expandingState: HighlightableStringExpandableContainerState.Collapsed,
      shouldExpandForHighlighting: true,
      json: {
        jqQuery: '',
        originalJson: bodyJson,
        processedJqResult: {
          jqQuery: '',
          processedHighlightableJson: bodyJson,
        },
      },
    };
  }
}

export class LogEntryMQTTCell extends React.Component<{ model: LogEntryMQTTCellModel }> {
  render() {
    const { title, command, message, params } = this.props.model.data;

    return (
      <div className="log-entry-cell">
        <Stack
          direction="row"
          spacing="8px"
          alignItems="center"
          className="network-response-succeeded"
          sx={{ maxWidth: 'fit-content' }}
        >
          <SyncAlt />
          <HighlightableStringView string={command} />
        </Stack>
        <div>
          <HighlightableStringView string={title} />
        </div>

        {params && params.length > 0 && (
          <div>
            <Stack direction="column" spacing="4px">
              {params.map((param, index) => (
                <HighlightableStringView key={index} string={param} />
              ))}
            </Stack>
          </div>
        )}

        {message && (
          <NetworkRequestBody
            data={message}
            onDataChange={(bodyChange) => {
              const change: DeepPartial<LogEntryMQTTCellModelData> = {
                message: bodyChange.change,
              };
              this.props.model.delegate?.onLogEntryCellModelChange({
                cellModel: this.props.model,
                change,
                postProcess: bodyChange.postProcess,
              });
            }}
            onRequestCreateNewColumn={(jqQuery) => {
              const c = this.props.model.onRequestCreateNewColumn;
              if (c) {
                c(jqQuery);
              }
            }}
          />
        )}
      </div>
    );
  }
}

function formatMessage(message: string): string {
  try {
    const parsedMessage = JSON.parse(message);
    return parsedMessage;
  } catch {
    return message;
  }
}
